import BlockedPage from '~/common/components/BlockedPage';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import useUrl from '~/common/hooks/useUrl';

import ScrollIntoViewContainer from '../common/components/ScrollIntoViewContainer';
import { StyledLoan1003LayoutContainer } from '../common/components/StyledComponents';
import RealEstateOwnedSummariesForm from './Form/RealEstateOwnedSummariesForm';
import type { RowToEdit } from './Form/RealEstateOwnedSummariesForm';
import NewREO from './NewREO';
import {
  RealEstateOwnedCrudGrid,
  type RowToDelete,
} from './components/RealEstateOwnedCrudGrid';
import {
  useDeleteRealEstateOwnedMutation,
  useGetRealEstateOwnedSummaryQuery,
} from './hooks';
import { useRealEstateOwnedStore } from './useRealEstateOwnedStore';

export default function RealEstateOwned1003() {
  const includeREOFlag = useNavFeatureFlags().IncludeREO;

  if (!includeREOFlag) {
    return <BlockedPage />;
  }

  return <RealEstateOwned1003Layout />;
}

function RealEstateOwned1003Layout() {
  const showForm = useRealEstateOwnedStore((state) => state.showForm);
  const rowToEdit = useRealEstateOwnedStore((state) => state.rowToEdit);
  const openForAdd = useRealEstateOwnedStore((state) => state.openForAdd);
  const openForEdit = useRealEstateOwnedStore((state) => state.openForEdit);

  const { isReadOnly } = useUrl();

  const { isLoading, data } = useGetRealEstateOwnedSummaryQuery();

  const featureFlags = useNavFeatureFlags();
  const isREO1003AndPropertyEnabled = featureFlags.IsREO1003AndPropertyEnabled;

  const isWriteAccessDisabled =
    useIsWriteAccessDisabled() || isReadOnly || !data?.hasWritePermission;

  const { mutate: deleteReo } = useDeleteRealEstateOwnedMutation();

  const handleEditMode = (row: RowToEdit, isEditBool: boolean): void => {
    if (isEditBool) {
      openForEdit(row);
      return;
    }

    openForAdd(row);
  };

  const handleDelete = async (row: RowToDelete[]): Promise<void> => {
    const {
      addressId: { addressId, addressLdId },
    } = row[0];
    deleteReo({ addressId, addressLdId });
  };

  return (
    <StyledLoan1003LayoutContainer>
      <RealEstateOwnedCrudGrid
        data={data}
        isLoading={isLoading}
        rowToEdit={rowToEdit}
        isWriteAccessDisabled={isWriteAccessDisabled}
        isREO1003AndPropertyEnabled={isREO1003AndPropertyEnabled}
        onEditMode={handleEditMode}
        onAddMode={() => {
          openForAdd(new NewREO(0));
        }}
        onDelete={handleDelete}
      />

      {showForm && rowToEdit && (
        <ScrollIntoViewContainer valueToWatch={rowToEdit}>
          <RealEstateOwnedSummariesForm
            key={
              typeof rowToEdit.addressId === 'object'
                ? (rowToEdit.addressId?.addressId ?? 0)
                : (rowToEdit.addressId ?? 0)
            }
            rowToEdit={rowToEdit}
            isWriteAccessDisabled={isWriteAccessDisabled}
            allBorrowerOptions={data?.availableBorrowers}
            dropdownOptions={data?.dropDownOptions}
          />
        </ScrollIntoViewContainer>
      )}
    </StyledLoan1003LayoutContainer>
  );
}
